import Container from '../layout/Container';
import styled from '@emotion/styled';
import Typography from '../text/Typography';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import SectionTitle from '../SectionTitle';
import PrimaryButton from '../buttons/PrimaryButton';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${spacing[5] + spacing[11]}px;
  padding-bottom: ${spacing[9]}px;
  max-width: 720px;

  ${breakpoints.desktop} {
    padding-bottom: ${spacing[11]}px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  margin-top: ${spacing[6]}px;

  ${breakpoints.tablet} {
    flex-direction: row;
    gap: ${spacing[4]}px;
    padding-left: ${spacing[5]}px;
  }
`;

const BodyTextWrapper = styled.div`
  margin-top: ${spacing[4]}px;

  ${breakpoints.tablet} {
    padding-left: ${spacing[5]}px;
  }
`;

const ImageCreditWrapper = styled.div`
  margin-top: ${spacing[4]}px;
  position: absolute;
  right: 0;
  bottom: 16px;
  padding-right: ${spacing[4]}px;

  ${breakpoints.tablet} {
    padding-right: ${spacing[6]}px;
  }

  ${breakpoints.desktop} {
    padding-right: ${spacing[11] + spacing[5]}px;
  }
`;

type Props = {
  bodyText?: string | null;
  imageUrl?: string | null;
  imageCredit?: string | null;
  backgroundImageAlt?: string;
  sectionTitle?: string | null;
  paragraph?: string | null;
  primaryButtonLabel?: string;
  primaryButtonHref?: string;
  secondaryButtonLabel?: string;
  secondaryButtonHref?: string;
  id?: string;
};

export default function FeatureHero({
  bodyText,
  imageUrl,
  imageCredit,
  backgroundImageAlt,
  sectionTitle,
  paragraph,
  primaryButtonLabel,
  primaryButtonHref,
  secondaryButtonLabel,
  secondaryButtonHref,
  id,
}: Props) {
  if (!sectionTitle && !paragraph && !imageUrl) return null;

  return (
    <Container
      id={id}
      disableVerticalPadding
      backgroundImage={imageUrl ?? undefined}
      backgroundImageAlt={backgroundImageAlt}
      imageOverlayPercentage={81}
      imageOverlayVariant="diagonal"
    >
      <ContentWrapper>
        {!!(sectionTitle || paragraph) && (
          <SectionTitle color={colors.white} title={sectionTitle ?? ''}>
            <Typography
              variant="h3"
              tag="h1"
              color={colors.white}
              style={{ marginTop: `${spacing[0]}px` }}
            >
              {paragraph}
            </Typography>
          </SectionTitle>
        )}

        {bodyText && (
          <BodyTextWrapper>
            <Typography variant="h6" color={colors.white}>
              {bodyText}
            </Typography>
          </BodyTextWrapper>
        )}

        {primaryButtonLabel && primaryButtonHref && (
          <ButtonWrapper>
            <PrimaryButton label={primaryButtonLabel} variant="solid" href={primaryButtonHref} />

            {secondaryButtonLabel && secondaryButtonHref && (
              <PrimaryButton
                label={secondaryButtonLabel}
                variant="outline"
                color="white"
                href={secondaryButtonHref}
              />
            )}
          </ButtonWrapper>
        )}

        {imageCredit && (
          <ImageCreditWrapper>
            <Typography color={colors.white} variant="caption">{`© ${imageCredit}`}</Typography>
          </ImageCreditWrapper>
        )}
      </ContentWrapper>
    </Container>
  );
}
